import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Card, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Alert, 
  Badge, 
  InputGroup,
  Spinner,
  Row,
  Col
} from 'react-bootstrap';
import { TagWithStats, sortTagsByUsage } from '../../Data/Tags';
import { tagService } from '../../../components/Tag/services/tagService';
import { showSuccess, showError, showDeleteConfirm, showInput } from '../../../Utilities/sweetAlert';
import './TagManagement.css';

const TagManagement: React.FC = () => {
  const [tags, setTags] = useState<TagWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'usage' | 'created'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Fetch all tags
  const fetchTags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await tagService.getAllTags();
      
      // Backend now returns tags with actual trail counts
      setTags(response.data.tags);

    } catch (err) {
      console.error('Error fetching tags:', err);
      setError('Failed to load tags. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Filter and sort tags
  const filteredAndSortedTags = React.useMemo(() => {
    let filtered = tags.filter(tag =>
      tag.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort based on selected criteria
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'usage':
          comparison = a.trailCount - b.trailCount;
          break;
        case 'created':
          comparison = a.id - b.id; // Assuming newer tags have higher IDs
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [tags, searchTerm, sortBy, sortOrder]);

  // Create new tag
  const handleCreateTag = async () => {
    const validation = tagService.validateTagName(newTagName);
    if (!validation.isValid) {
      showError('Invalid Tag Name', validation.error || 'Invalid tag name');
      return;
    }

    const nameExists = await tagService.checkTagNameExists(newTagName, tags);
    if (nameExists) {
      showError('Tag Exists', 'A tag with this name already exists.');
      return;
    }

    try {
      setCreateLoading(true);
      const response = await tagService.createTag(newTagName.trim());
      
      const createdTag: TagWithStats = {
        id: response.data.tag.identifiers[0].id,
        name: newTagName.trim(),
        trailCount: 0
      };

      setTags(prev => [createdTag, ...prev]);
      setNewTagName('');
      setShowCreateModal(false);
      showSuccess('Tag Created', `Tag "${createdTag.name}" has been created successfully.`);
    } catch (err) {
      console.error('Error creating tag:', err);
      showError('Creation Failed', 'Failed to create tag. Please try again.');
    } finally {
      setCreateLoading(false);
    }
  };

  // Delete tag
  const handleDeleteTag = async (tag: TagWithStats) => {
    const result = await showDeleteConfirm(
      'Delete Tag?',
      `Are you sure you want to delete the tag "${tag.name}"? This action cannot be undone and will remove the tag from all trails.${
        tag.trailCount > 0 
          ? `\n\nThis tag is currently used by ${tag.trailCount} trail(s) and will be removed from all of them.` 
          : ''
      }`,
      'Yes, delete it',
      'Cancel'
    );

    if (result.isConfirmed) {
      try {
        await tagService.deleteTag(tag.id);
        setTags(prev => prev.filter(t => t.id !== tag.id));
        showSuccess('Tag Deleted', `Tag "${tag.name}" has been deleted successfully and removed from all trails.`);
      } catch (err) {
        console.error('Error deleting tag:', err);
        showError('Delete Failed', 'Failed to delete tag. Please try again.');
      }
    }
  };

  // Quick create tag using SweetAlert
  const handleQuickCreate = async () => {
    const result = await showInput(
      'Create New Tag',
      'Enter tag name...',
      '',
      'Create Tag'
    );

    if (result.isConfirmed && result.value) {
      setNewTagName(result.value);
      await handleCreateTag();
    }
  };

  // Statistics
  const totalTags = tags.length;
  const totalUsage = tags.reduce((sum, tag) => sum + tag.trailCount, 0);
  const averageUsage = totalTags > 0 ? (totalUsage / totalTags).toFixed(1) : 0;
  const mostUsedTag = tags.length > 0 ? sortTagsByUsage(tags)[0] : null;

  if (loading) {
    return (
      <Container fluid className="admin-page-container py-4">
        <div className="text-center py-5">
          <Spinner animation="border" role="status" style={{ color: 'var(--primary-medium)' }}>
            <span className="visually-hidden">Loading tags...</span>
          </Spinner>
          <p className="mt-3 text-muted">Loading tag management...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="admin-page-container py-4">
        <Alert variant="danger" className="mb-4">
          {error}
          <div className="mt-3">
            <Button variant="outline-danger" onClick={fetchTags}>
              Retry
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="admin-page-container py-4">
      <div className="tag-management">
        {/* Header */}
        <div className="page-header mb-4">
          <Row>
            <Col>
              <h1>Tag Management</h1>
              <p className="text-muted">
                Manage trail tags, create new ones, and view usage statistics.
              </p>
            </Col>
            <Col xs="auto">
              <Button 
                variant="primary" 
                onClick={() => setShowCreateModal(true)}
                className="me-2"
              >
                + Create Tag
              </Button>
            </Col>
          </Row>
        </div>

        {/* Statistics Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body className="text-center">
                <h3 className="stat-number">{totalTags}</h3>
                <p className="stat-label">Total Tags</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body className="text-center">
                <h3 className="stat-number">{totalUsage}</h3>
                <p className="stat-label">Total Usage</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body className="text-center">
                <h3 className="stat-number">{averageUsage}</h3>
                <p className="stat-label">Avg Usage</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card">
              <Card.Body className="text-center">
                <h3 className="stat-number text-truncate">
                  {mostUsedTag?.name || 'None'}
                </h3>
                <p className="stat-label">Most Used</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Search and Sort Controls */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={6}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="fas fa-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search tags..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'usage' | 'created')}
                >
                  <option value="name">Sort by Name</option>
                  <option value="usage">Sort by Usage</option>
                  <option value="created">Sort by Created</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tags Table */}
        <Card>
          <Card.Header>
            <h5 className="mb-0">
              Tags ({filteredAndSortedTags.length})
            </h5>
          </Card.Header>
          <Card.Body className="p-0">
            {filteredAndSortedTags.length === 0 ? (
              <div className="text-center p-4">
                <i className="fas fa-tags fa-3x text-muted mb-3"></i>
                <h5 className="text-muted">No tags found</h5>
                <p className="text-muted">
                  {searchTerm 
                    ? 'Try adjusting your search terms or create a new tag.' 
                    : 'Get started by creating your first tag.'
                  }
                </p>
                <Button 
                  variant="outline-primary" 
                  onClick={() => setShowCreateModal(true)}
                >
                  Create First Tag
                </Button>
              </div>
            ) : (
              <Table striped hover responsive>
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>ID</th>
                    <th style={{ width: '40%' }}>Name</th>
                    <th style={{ width: '20%' }}>Usage</th>
                    <th style={{ width: '20%' }}>Status</th>
                    <th style={{ width: '10%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedTags.map((tag) => (
                    <tr key={tag.id}>
                      <td>
                        <Badge bg="secondary">#{tag.id}</Badge>
                      </td>
                      <td>
                        <strong>{tag.name}</strong>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                                                  <Badge 
                          bg={
                            tag.trailCount === 0 
                              ? 'secondary' 
                              : tag.trailCount > 5 
                              ? 'success' 
                              : 'primary'
                          }
                          className="me-2"
                        >
                          {tag.trailCount} trails
                        </Badge>
                        </div>
                      </td>
                      <td>
                        <Badge 
                          bg={tag.trailCount > 0 ? 'success' : 'warning'}
                        >
                          {tag.trailCount > 0 ? 'In Use' : 'Unused'}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteTag(tag)}
                          title="Delete tag"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        {/* Create Tag Modal */}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Create New Tag</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group>
                <Form.Label>Tag Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter tag name..."
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  maxLength={50}
                  autoFocus
                />
                <Form.Text className="text-muted">
                  2-50 characters. Letters, numbers, spaces, hyphens, and underscores only.
                </Form.Text>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowCreateModal(false)}
              disabled={createLoading}
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleCreateTag}
              disabled={createLoading || !newTagName.trim()}
            >
              {createLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                'Create Tag'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
};

export default TagManagement;